import React, { useState } from 'react';
import {
  useMoodboards, createMoodboard, deleteMoodboard, updateMoodboard,
  addMoodboardTile, saveMoodboardTiles, useItemAssets,
} from '../data/firestore';
import AssetImage, { useAssetUrl } from './AssetImage';
import Modal from './Modal';
import { clipFromWeb, isUploadConfigured } from '../data/uploadService';

// Moodboards for one space: image grids with per-tile notes. Tiles come from
// the space's uploaded photo assets, or are clipped from any web page
// (Pinterest, Houzz, retailers…) — the backend saves a copy into S3 so the
// board keeps working even if the source site pulls the image.

const IMAGE_RX = /\.(jpe?g|png|gif|webp|avif)(\?.*)?$/i;

export function TileImage({ tile, onClick }) {
  if (tile.type === 'url') {
    return <img src={tile.url} alt={tile.name || ''} loading="lazy" onClick={onClick} />;
  }
  return <AssetImage s3Key={tile.s3Key} alt={tile.name || ''} onClick={onClick} />;
}

export function Lightbox({ tile, onClose, onSaveNote, onRemove, canEdit }) {
  const [note, setNote] = useState(tile.note || '');
  const assetUrl = useAssetUrl(tile.type === 'asset' ? tile.s3Key : null);
  const src = tile.type === 'url' ? tile.url : assetUrl;
  return (
    <Modal onClose={onClose}>
      <div className="ah-lightbox">
        {src ? <img src={src} alt={tile.name || ''} /> : <div className="ah-tile-placeholder">🖼</div>}
        <div className="ah-lightbox-side">
          {tile.name && <strong>{tile.name}</strong>}
          {canEdit ? (
            <>
              <textarea
                className="cs-input"
                rows={5}
                placeholder="Notes — finish, dimensions, where it goes…"
                value={note}
                onChange={(e) => setNote(e.target.value)}
              />
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="cs-btn cs-btn-primary cs-btn-sm" onClick={() => { onSaveNote(note); onClose(); }}>
                  Save note
                </button>
                <span style={{ flex: 1 }} />
                <button className="cs-btn cs-btn-ghost cs-btn-sm" onClick={() => { onRemove(); onClose(); }}>
                  Remove tile
                </button>
              </div>
            </>
          ) : (
            tile.note && <p className="cs-help" style={{ marginTop: 6 }}>{tile.note}</p>
          )}
          {tile.url && (
            <a href={tile.url} target="_blank" rel="noreferrer" className="cs-help" style={{ fontSize: 12 }}>
              source ↗
            </a>
          )}
        </div>
      </div>
    </Modal>
  );
}

function Board({ spaceId, board, assets, canEdit }) {
  const [adding, setAdding] = useState(false);
  const [urlInput, setUrlInput] = useState('');
  const [noteInput, setNoteInput] = useState('');
  const [clipping, setClipping] = useState(false);
  const [clipError, setClipError] = useState('');
  const [openTile, setOpenTile] = useState(null);
  const [renaming, setRenaming] = useState(false);
  const [title, setTitle] = useState(board.title);

  // Add a tile from any pasted link. Preferred path: backend clips the page's
  // main image into S3 (survives source-site changes, avoids hotlink blocks).
  // Direct image URLs fall back to a plain url tile if the clip fails.
  const addFromLink = async (url, note) => {
    setClipError('');
    if (isUploadConfigured()) {
      setClipping(true);
      try {
        const clip = await clipFromWeb({ url, spaceId });
        await addMoodboardTile(spaceId, board.id, {
          type: 'asset',
          s3Key: clip.s3Key,
          url: clip.sourceUrl,
          name: clip.title || url.split('/').pop().split('?')[0] || '',
          note,
        });
        return true;
      } catch (err) {
        if (!IMAGE_RX.test(url)) {
          setClipError(err.message || 'Could not find an image at that link.');
          return false;
        }
        // direct image link → fall through to a plain url tile
      } finally {
        setClipping(false);
      }
    }
    if (!IMAGE_RX.test(url)) {
      setClipError('Could not clip that page — paste a direct image URL instead.');
      return false;
    }
    await addMoodboardTile(spaceId, board.id, {
      type: 'url', url, note,
      name: url.split('/').pop().split('?')[0],
    });
    return true;
  };

  const tiles = board.tiles || [];
  const photoAssets = (assets || []).filter((a) => a.kind === 'photo' && a.s3Key);
  const usedKeys = new Set(tiles.filter((t) => t.type === 'asset').map((t) => t.s3Key));

  const patchTile = (id, patch) =>
    saveMoodboardTiles(spaceId, board.id, tiles.map((t) => (t.id === id ? { ...t, ...patch } : t)));
  const removeTile = (id) =>
    saveMoodboardTiles(spaceId, board.id, tiles.filter((t) => t.id !== id));

  return (
    <div className="cs-card" style={{ padding: 16, marginTop: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        {renaming && canEdit ? (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              updateMoodboard(spaceId, board.id, { title: title.trim() || board.title });
              setRenaming(false);
            }}
            style={{ display: 'flex', gap: 6 }}
          >
            <input className="cs-input" value={title} onChange={(e) => setTitle(e.target.value)} autoFocus />
            <button className="cs-btn cs-btn-primary cs-btn-sm" type="submit">Save</button>
          </form>
        ) : (
          <h3
            style={{ margin: 0, fontSize: 16, cursor: canEdit ? 'pointer' : 'default' }}
            onClick={canEdit ? () => setRenaming(true) : undefined}
            title={canEdit ? 'Click to rename' : undefined}
          >
            {board.title}
          </h3>
        )}
        <span className="cs-help" style={{ fontSize: 12 }}>{tiles.length} tile{tiles.length === 1 ? '' : 's'}</span>
        <span style={{ flex: 1 }} />
        {canEdit && (
          <>
            <button className="cs-btn cs-btn-outline cs-btn-sm" onClick={() => setAdding((v) => !v)}>
              {adding ? 'Close' : '+ Add tiles'}
            </button>
            <button
              className="cs-btn cs-btn-ghost cs-btn-sm"
              onClick={() => {
                // eslint-disable-next-line no-alert
                if (window.confirm(`Delete moodboard “${board.title}”?`)) deleteMoodboard(spaceId, board.id);
              }}
            >
              Delete
            </button>
          </>
        )}
      </div>

      {adding && canEdit && (
        <div style={{ marginTop: 12 }}>
          <form
            style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}
            onSubmit={async (e) => {
              e.preventDefault();
              const url = urlInput.trim();
              if (!url || clipping) return;
              const ok = await addFromLink(url, noteInput.trim());
              if (ok) {
                setUrlInput('');
                setNoteInput('');
              }
            }}
          >
            <input
              className="cs-input"
              style={{ flex: '2 1 260px' }}
              placeholder="Paste any link — Pinterest pin, Houzz photo, product page, image URL…"
              value={urlInput}
              onChange={(e) => { setUrlInput(e.target.value); setClipError(''); }}
            />
            <input
              className="cs-input"
              style={{ flex: '1 1 160px' }}
              placeholder="Note (optional)"
              value={noteInput}
              onChange={(e) => setNoteInput(e.target.value)}
            />
            <button className="cs-btn cs-btn-primary cs-btn-sm" type="submit" disabled={!urlInput.trim() || clipping}>
              {clipping ? 'Clipping…' : 'Clip it'}
            </button>
          </form>
          {clipError && (
            <p className="cs-help" style={{ margin: '8px 0 0', fontSize: 12, color: '#c0392b' }}>
              {clipError}
            </p>
          )}

          {photoAssets.length > 0 && (
            <>
              <p className="cs-help" style={{ margin: '12px 0 6px', fontSize: 12 }}>
                …or pick from this space's uploaded photos:
              </p>
              <div className="ah-mood-picker">
                {photoAssets.map((a) => (
                  <button
                    key={a.id}
                    type="button"
                    className={`ah-mood-pick ${usedKeys.has(a.s3Key) ? 'used' : ''}`}
                    title={usedKeys.has(a.s3Key) ? `${a.name} (already on this board)` : a.name}
                    disabled={usedKeys.has(a.s3Key)}
                    onClick={() => addMoodboardTile(spaceId, board.id, { type: 'asset', s3Key: a.s3Key, name: a.name })}
                  >
                    <AssetImage s3Key={a.s3Key} alt={a.name} />
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {!tiles.length && !adding && (
        <div className="cs-empty" style={{ marginTop: 12, padding: 14 }}>
          Empty board — add inspo images or uploaded photos.
        </div>
      )}

      <div className="ah-mood-grid">
        {tiles.map((t) => (
          <figure key={t.id} className="ah-mood-tile" onClick={() => setOpenTile(t)}>
            <TileImage tile={t} />
            {(t.note || t.name) && <figcaption>{t.note || t.name}</figcaption>}
          </figure>
        ))}
      </div>

      {openTile && (
        <Lightbox
          tile={openTile}
          canEdit={canEdit}
          onClose={() => setOpenTile(null)}
          onSaveNote={(note) => patchTile(openTile.id, { note })}
          onRemove={() => removeTile(openTile.id)}
        />
      )}
    </div>
  );
}

export default function Moodboards({ space, canEdit = true }) {
  const { docs: boards } = useMoodboards(space.id);
  const { docs: assets } = useItemAssets(space.id);
  const [newTitle, setNewTitle] = useState('');

  return (
    <div>
      {canEdit && (
        <form
          style={{ display: 'flex', gap: 8, maxWidth: 460 }}
          onSubmit={async (e) => {
            e.preventDefault();
            if (!newTitle.trim()) return;
            await createMoodboard(space.id, newTitle.trim());
            setNewTitle('');
          }}
        >
          <input
            className="cs-input"
            placeholder="New moodboard — e.g. “Tile & stone”, “Warm minimal”…"
            value={newTitle}
            onChange={(e) => setNewTitle(e.target.value)}
          />
          <button className="cs-btn cs-btn-primary" type="submit" disabled={!newTitle.trim()}>
            Create
          </button>
        </form>
      )}

      {boards === null && <p className="cs-help" style={{ marginTop: 14 }}>Loading…</p>}
      {boards && !boards.length && (
        <div className="cs-empty" style={{ marginTop: 14 }}>
          {canEdit ? 'No moodboards yet — create one above.' : 'No moodboards yet.'}
        </div>
      )}
      {(boards || []).map((b) => (
        <Board key={b.id} spaceId={space.id} board={b} assets={assets} canEdit={canEdit} />
      ))}
    </div>
  );
}
